import React, { useEffect, useState } from "react";
import axios, { CancelToken } from "axios";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";
import {
  GetBookingCategories,
  PostBookingCategory,
  PutBookingCategory,
} from "api/rpc/2024-04/facilityAdmin/teesheet/bookingCategory";

import { showError, showSuccess } from "redux/actions/ui";
import { IBookingCategory } from "redux/reducers/models/teetime";
import { useAppDispatch, useAppSelector } from "hooks/redux";

import Page from "components/page/Page";
import Input from "components/form/input/Input";
import Sheet from "components/sheet/Sheet";
import DataTable from "components/dataTable/DataTable";

interface IBookingCategoriesState {
  categories: Array<IBookingCategory>;
  selectedCategory: IBookingCategory;
  categoryTitle: string;
  editBookingCategoryModalVisible: boolean;
  newBookingCategoryModalVisible: boolean;
}

export default function BookingCategories() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { authStore } = useAppSelector(store => store);
  const permissions = authStore?.user?.permissions;

  const [state, setState] = useState<IBookingCategoriesState>({
    categories: undefined,
    selectedCategory: null,
    categoryTitle: "",
    editBookingCategoryModalVisible: false,
    newBookingCategoryModalVisible: false,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();

    void loadBookingCategories(source.token);

    return () => {
      source.cancel();
    };
  }, []);

  async function loadBookingCategories(token?: CancelToken) {
    const categoryRes = await GetBookingCategories(null, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (categoryRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading booking categories")));
      return;
    }

    setState(prevState => ({ ...prevState, categories: categoryRes.data }));
  }

  async function updateBookingCategory() {
    const categoryRes = await PutBookingCategory(
      {
        id: state.selectedCategory.id,
        title: state.categoryTitle,
      },
      true,
    );

    if (categoryRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating booking category")));
      return;
    }

    dispatch(showSuccess(t("Booking category updated successfully")));
    setState(prevState => ({
      ...prevState,
      selectedCategory: null,
      categoryTitle: "",
      editBookingCategoryModalVisible: false,
    }));
    void loadBookingCategories();
  }

  async function createBookingCategory() {
    const categoryRes = await PostBookingCategory(
      {
        title: state.categoryTitle,
      },
      true,
    );

    if (categoryRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating booking category")));
      return;
    }

    dispatch(showSuccess(t("Booking category created successfully")));
    setState(prevState => ({
      ...prevState,
      categoryTitle: "",
      newBookingCategoryModalVisible: false,
    }));
    void loadBookingCategories();
  }

  function handleEditBookingCategory(category: IBookingCategory) {
    setState(prevState => ({
      ...prevState,
      selectedCategory: category,
      categoryTitle: category.title,
      editBookingCategoryModalVisible: true,
    }));
  }

  function handleNewBookingCategory() {
    setState(prevState => ({
      ...prevState,
      categoryTitle: "",
      newBookingCategoryModalVisible: true,
    }));
  }

  function handleCancelEdit() {
    setState(prevState => ({
      ...prevState,
      selectedCategory: null,
      categoryTitle: "",
      editBookingCategoryModalVisible: false,
    }));
  }

  function handleCancelNew() {
    setState(prevState => ({
      ...prevState,
      categoryTitle: "",
      newBookingCategoryModalVisible: false,
    }));
  }

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setState(prevState => ({ ...prevState, [id]: value }));
  }

  const primaryAction = {
    content: t("New Booking Category"),
    action: handleNewBookingCategory,
  };

  return (
    <Page
      title={t("Booking Categories")}
      narrow
      primaryAction={permissions?.booking_category_create ? primaryAction : null}
    >
      <DataTable
        columns={[
          { label: t("Title"), width: "100%" },
        ]}
        loading={state.categories === undefined}
      >
        {state.categories ? (
          <>
            {state.categories.map(category => {
              return (
                <tr key={category.id} onClick={() => handleEditBookingCategory(category)}>
                  <td>
                    <p className="table-cell-lead-text">{category.title}</p>
                  </td>
                </tr>
              );
            })}
          </>
        ) : null}
      </DataTable>

      <Sheet
        title={t("Edit Booking Category")}
        open={state.editBookingCategoryModalVisible}
        size="small"
        onCancel={handleCancelEdit}
        onOk={updateBookingCategory}
        closable
        backDropCancel
        cancelText={t("Cancel")}
        okText={t("Update")}
      >
        <Input
          value={state.categoryTitle}
          label={t("Title")}
          id="categoryTitle"
          onChange={handleInputChange}
        />
      </Sheet>

      <Sheet
        title={t("New Booking Category")}
        open={state.newBookingCategoryModalVisible}
        size="small"
        onCancel={handleCancelNew}
        onOk={createBookingCategory}
        closable
        backDropCancel
        cancelText={t("Cancel")}
        okText={t("Create")}
      >
        <Input
          value={state.categoryTitle}
          label={t("Title")}
          id="categoryTitle"
          onChange={handleInputChange}
        />
      </Sheet>
    </Page>
  );
}
